import { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  getConfig, saveConfig, clearConfig, isConfigured,
  ACCOUNTS, CASH_ACCOUNTS, CATEGORIES, STORAGE_KEYS,
  MAIN_CATEGORIES, mainCategory, CATEGORY_COLORS, DEFAULT_BUDGET, getBudgetPlan, saveBudgetPlan,
  getExpenseMonth, isInExpensePeriod, isToday, isThisCalendarWeek, getWeekLabel
} from './config';
import { fetchData, writeCorrection, writeManualTx } from './api';
import { parseSMS, extractLatestBalances } from './parser';
import { categorize } from './categorizer';
import './App.css';

// ── HELPERS ───────────────────────────────────────────────────────────────────
const fmt    = n => (n || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = n => (n || 0).toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const pct    = (a, b) => b > 0 ? Math.min((a / b) * 100, 100) : 0;

// Card transactions = real spend. Account transactions = transfers/movements.
const isCardTx = (t) => {
  const acc = ACCOUNTS[t.source];
  return acc ? acc.type === 'card' : String(t.source).toLowerCase().includes('card');
};
const isSalary = (t) => t.direction === 'credit' && String(t.description || '').toLowerCase().includes('salary');

const isCCPayment = (t) => {
  const text = String(t.description || t.raw_text || '').toLowerCase();
  return text.includes('payment towards credit card') || text.includes('thank you for the payment') || text.includes('card payment received') || text.includes('payment received');
};

// Extract date+time from raw SMS text, fallback to tx_date
const extractDateTime = (rawText, txDate) => {
  if (rawText) {
    const match = String(rawText).match(/(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2}:\d{2})/);
    if (match) return `${match[1]} ${match[2]}`;
    const dateOnly = String(rawText).match(/(\d{2}\/\d{2}\/\d{4})/);
    if (dateOnly) return dateOnly[1];
  }
  if (txDate) {
    const s = String(txDate);
    if (s.includes('T')) {
      const d = new Date(s);
      if (!isNaN(d)) return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
    }
    return s;
  }
  return '';
};

// Get AED amount — use amount_aed if available, otherwise fall back to amount
const aed = (t) => t.amount_aed || t.amount || 0;

// AED totals per main category (Food, Shopping, … Other)
const byMain = (txs) => txs.reduce((acc, t) => {
  const c = mainCategory(t.category);
  acc[c] = (acc[c] || 0) + aed(t);
  return acc;
}, {});

function mapTx(r) {
  return {
    id: r.id, timestamp: r.timestamp, tx_date: r.tx_date,
    display_date: extractDateTime(r.raw_text, r.tx_date),
    source: r.source, source_type: r.source_type, account_last4: r.account_last4,
    description: r.description || '',
    amount:     parseFloat(r.amount) || 0,
    amount_aed: (parseFloat(r.amount_aed) && parseFloat(r.amount_aed) !== parseFloat(r.amount)) ? parseFloat(r.amount_aed) : (r.currency === 'AED' || !r.currency) ? parseFloat(r.amount) || 0 : 0,
    direction:  r.direction || 'debit',
    currency:   r.currency || 'AED',
    balance:    parseFloat(r.balance) || null,
    category:    r.category    || 'Other',
    subcategory: r.subcategory || 'Miscellaneous',
    confidence:  parseFloat(r.confidence) || 0,
    corrected:   r.corrected === 'TRUE' || r.corrected === true,
    origin:    r.origin, raw_text: r.raw_text,
  };
}

// ── SETUP SCREEN ──────────────────────────────────────────────────────────────
function SetupScreen({ onComplete }) {
  const [url,     setUrl]     = useState('');
  const [token,   setToken]   = useState('');
  const [error,   setError]   = useState('');
  const [testing, setTesting] = useState(false);

  const connect = async () => {
    if (!url || !token) { setError('Both fields are required.'); return; }
    setTesting(true); setError('');
    try {
      const res  = await fetch(`${url}?action=getData`);
      const data = await res.json();
      if (data.status !== 'success') throw new Error(data.message || 'Connection failed');
      saveConfig({ appsScriptUrl: url, secretToken: token });
      onComplete();
    } catch (e) {
      setError('Could not connect. Check your Apps Script URL is deployed and accessible.');
    }
    setTesting(false);
  };

  return (
    <div className="setup-wrap">
      <div className="setup-card">
        <div className="setup-logo">💳</div>
        <h1>Expense Tracker</h1>
        <p className="setup-sub">Enter your backend credentials to connect. These are stored only in this browser — never in the app code.</p>
        <div className="field">
          <label>Apps Script Web App URL</label>
          <input type="url" placeholder="https://script.google.com/macros/s/..." value={url} onChange={e => setUrl(e.target.value)} />
          <span className="hint">Your Google Apps Script → Deploy → Web App URL</span>
        </div>
        <div className="field">
          <label>Secret Token</label>
          <input type="password" placeholder="Your secret token" value={token} onChange={e => setToken(e.target.value)} />
          <span className="hint">The token set in your Apps Script</span>
        </div>
        {error && <div className="alert-error">{error}</div>}
        <button className="btn-primary btn-full" onClick={connect} disabled={testing}>
          {testing ? 'Connecting…' : 'Connect & Continue'}
        </button>
        <div className="setup-note">
          Your credentials are stored in browser localStorage only and never sent anywhere except your own Google Apps Script.
        </div>
      </div>
    </div>
  );
}

// ── PROGRESS BAR ──────────────────────────────────────────────────────────────
function ProgressBar({ value, max, color }) {
  const p = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const c = p >= 100 ? 'var(--danger)' : p >= 80 ? 'var(--warning)' : color || 'var(--accent)';
  return (
    <div className="pbar">
      <div className="pbar-fill" style={{ width: `${p}%`, background: c }} />
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color, onClick }) {
  return (
    <div className={`stat-card ${onClick ? 'clickable' : ''}`} onClick={onClick}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: color || 'var(--text)' }}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [configured,   setConfigured]   = useState(isConfigured());
  const [tab,          setTab]          = useState('home');
  const [transactions, setTx]          = useState([]);
  const [smsLog,       setSmsLog]       = useState([]);
  const [corrections,  setCorrections]  = useState([]);
  const [cashOverrides, setCashOverrides] = useState({});
  const [loading,      setLoading]      = useState(false);
  const [syncing,      setSyncing]      = useState(false);
  const [error,        setError]        = useState('');
  const [notif,        setNotif]        = useState(null);
  const [correcting,   setCorrecting]   = useState(null);
  const [showManual,   setShowManual]   = useState(false);
  const [showCash,     setShowCash]     = useState(false);

  // Salary stored in localStorage
  const [salary, setSalaryState] = useState(() => parseFloat(localStorage.getItem(STORAGE_KEYS.SALARY)) || 0);
  const setSalary = v => { localStorage.setItem(STORAGE_KEYS.SALARY, v); setSalaryState(v); };

  // Fixed monthly budget, stored in localStorage
  const [budgetPlan, setBudgetPlan] = useState(getBudgetPlan);
  const updateBudgetPlan = plan => { saveBudgetPlan(plan); setBudgetPlan(plan); };

  // Manual cash (Faysal, Invest Nation)
  const [manualCash, setManualCash] = useState({
    faysal:        parseFloat(localStorage.getItem(STORAGE_KEYS.CASH_FAYSAL)) || 0,
    invest_nation: parseFloat(localStorage.getItem(STORAGE_KEYS.CASH_INVEST)) || 0,
  });
  const updateManualCash = (key, val) => {
    const storageKey = key === 'faysal' ? STORAGE_KEYS.CASH_FAYSAL : STORAGE_KEYS.CASH_INVEST;
    localStorage.setItem(storageKey, val);
    setManualCash(prev => ({ ...prev, [key]: parseFloat(val) || 0 }));
  };

  // Expense month
  const [expPeriod, setExpPeriod] = useState(getExpenseMonth());

  const notify = (msg, type = 'success') => { setNotif({ msg, type }); setTimeout(() => setNotif(null), 3500); };

  // ── LOAD DATA ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await fetchData();
      setTx((data.transactions || []).filter(r => r.id).map(mapTx));
      setSmsLog((data.smsLog || []).map((r, i) => ({
        row: i + 2, timestamp: r.timestamp, bank: r.bank, body: r.body, date: r.date, status: r.status,
      })));
      setCorrections((data.corrections || []).map(r => ({
        date: r.date, description_pattern: r.description_pattern,
        old_category: r.old_category, old_subcategory: r.old_subcategory,
        new_category: r.new_category, new_subcategory: r.new_subcategory,
      })));
    } catch (e) { setError('Failed to load: ' + e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { if (configured) load(); }, [configured, load]);

  // ── PROCESS PENDING SMS ───────────────────────────────────────────────────
  const processPending = async () => {
    const pendingNow = smsLog.filter(s => s.status === 'pending').length;
    if (!pendingNow) { notify('No pending SMS to process'); return; }
    setSyncing(true);
    try {
      const { appsScriptUrl, secretToken } = (await import('./config')).getConfig();
      const res  = await fetch(`${appsScriptUrl}?action=processPending&token=${secretToken}`);
      const data = await res.json();
      if (data.status !== 'success') throw new Error(data.message || 'Server error');
      notify(`✓ Processed ${data.count} transaction${data.count !== 1 ? 's' : ''}`);
      await load();
    } catch (e) { notify('Processing failed: ' + e.message, 'error'); }
    setSyncing(false);
  };

  // ── CORRECTION ────────────────────────────────────────────────────────────
  const applyCorrection = async (txId, newCat, newSub) => {
    const tx = transactions.find(t => t.id === txId);
    if (!tx) return;
    setTx(prev => prev.map(t => t.id === txId ? { ...t, category: newCat, subcategory: newSub, corrected: true } : t));
    const rule = {
      date: new Date().toISOString().split('T')[0], description_pattern: tx.description,
      old_category: tx.category, old_subcategory: tx.subcategory,
      new_category: newCat, new_subcategory: newSub,
    };
    setCorrections(prev => [...prev, rule]);
    await writeCorrection(rule);
    setCorrecting(null);
    notify('Category updated — rule saved for future transactions');
  };

  // ── COMPUTED DATA ─────────────────────────────────────────────────────────
  const debits = useMemo(() => transactions.filter(t => t.direction === 'debit'), [transactions]);

  // Card spend = real expenses. Account flows = transfers/movements.
  const cardDebits   = useMemo(() => debits.filter(t => isCardTx(t) && !isCCPayment(t)), [debits]);
  const acctDebits   = useMemo(() => debits.filter(t => !isCardTx(t)), [debits]);
  const salaryTxs    = useMemo(() => transactions.filter(isSalary), [transactions]);

  // Daily/Weekly/Monthly — CARDS ONLY (real spend) — using AED amounts. Always the current period.
  const curPeriod  = useMemo(() => getExpenseMonth(), []);
  const todayTxs   = useMemo(() => cardDebits.filter(t => isToday(t.tx_date)), [cardDebits]);
  const weekTxs    = useMemo(() => cardDebits.filter(t => isThisCalendarWeek(t.tx_date)), [cardDebits]);
  const monthTxs   = useMemo(() => cardDebits.filter(t => isInExpensePeriod(t.tx_date, curPeriod.year, curPeriod.month)), [cardDebits, curPeriod]);

  // Account flows for the month
  const monthAcctFlows = useMemo(() => acctDebits.filter(t => isInExpensePeriod(t.tx_date, expPeriod.year, expPeriod.month)), [acctDebits, expPeriod]);
  const monthAcctTotal = useMemo(() => monthAcctFlows.reduce((s, t) => s + aed(t), 0), [monthAcctFlows]);

  const todayTotal = useMemo(() => todayTxs.reduce((s, t) => s + aed(t), 0), [todayTxs]);
  const weekTotal  = useMemo(() => weekTxs.reduce((s, t) => s + aed(t), 0), [weekTxs]);
  const monthTotal = useMemo(() => monthTxs.reduce((s, t) => s + aed(t), 0), [monthTxs]);

  const pendingCount   = useMemo(() => smsLog.filter(s => s.status === 'pending').length, [smsLog]);

  // Category breakdown helper — uses AED amounts
  const byCat = (txList) => txList.reduce((acc, t) => {
    const k = `${t.category}|${t.subcategory}`;
    acc[k] = (acc[k] || 0) + aed(t);
    return acc;
  }, {});

  const monthByMain = useMemo(() => byMain(monthTxs), [monthTxs]);

  // Budget used = fixed expenses + MTD card spend
  const fixedTotal  = budgetPlan.items.reduce((s, i) => s + i.amount, 0);
  const budgetUsed  = fixedTotal + monthTotal;

  // Auto balances from SMS
  const smsBalances = useMemo(() => extractLatestBalances(smsLog), [smsLog]);

  // Net cash position
  const cashPosition = useMemo(() => {
    let total = 0;
    for (const acc of CASH_ACCOUNTS) {
      if (acc.auto) {
        const override = cashOverrides[acc.key];
        const smsVal   = smsBalances[acc.key]?.amount || 0;
        total += override !== undefined ? override : smsVal;
      } else {
        total += manualCash[acc.key] || 0;
      }
    }
    return total;
  }, [smsBalances, cashOverrides, manualCash]);

  // Month-on-month trend data (last 6 expense periods) — CARDS ONLY, AED amounts
  const trendData = useMemo(() => {
    const periods = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const ep = getExpenseMonth(d);
      const txs   = cardDebits.filter(t => isInExpensePeriod(t.tx_date, ep.year, ep.month));
      const total = txs.reduce((s, t) => s + aed(t), 0);
      const cats  = Object.fromEntries(Object.entries(byMain(txs)).map(([c, v]) => [c, Math.round(v)]));
      periods.push({ name: new Date(ep.year, ep.month).toLocaleString('en-AE', { month: 'short' }), spend: Math.round(total), ...cats });
    }
    return periods;
  }, [cardDebits]);

  if (!configured) return <SetupScreen onComplete={() => setConfigured(true)} />;

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="app">
      {notif && <div className={`notif notif-${notif.type}`}>{notif.msg}</div>}

      {/* NAV */}
      <nav className="nav">
        <span className="nav-brand">💳</span>
        <div className="nav-tabs">
          {[['home','Overview'],['budget','Budget & Trends'],['flows','Account Flows'],['cash','Cash'],['txns','Transactions'],['settings','Settings']].map(([id, lbl]) => (
            <button key={id} className={`nav-tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{lbl}</button>
          ))}
        </div>
        <div className="nav-right">
          {pendingCount > 0 && <span className="badge-warn">{pendingCount} pending</span>}
          <button className="btn-primary" onClick={processPending} disabled={syncing}>{syncing ? 'Processing…' : '⚡ Process SMS'}</button>
          <button className="btn-ghost icon-btn" onClick={load} disabled={loading} title="Refresh">{loading ? '…' : '↻'}</button>
        </div>
      </nav>

      <main className="main">
        {error && <div className="alert-error">{error}</div>}

        {/* ══ OVERVIEW (MAIN PAGE) ═════════════════════════════════════════ */}
        {tab === 'home' && (
          <div>
            <div className="page-hdr">
              <div>
                <h2>Overview</h2>
                <div className="page-sub">{new Date().toLocaleDateString('en-AE', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
                <button className="btn-secondary mt-12" onClick={() => setShowManual(true)}>+ Add transaction</button>
              </div>
              <button className="budget-summary" onClick={() => setTab('budget')} title="Open Budget & Trends">
                <div className="stat-label">Monthly budget · {curPeriod.label}</div>
                <div className="budget-summary-vals">
                  <span className={budgetUsed > budgetPlan.total ? 'text-danger' : ''}>AED {fmtInt(budgetUsed)}</span>
                  <span className="text-muted"> / {fmtInt(budgetPlan.total)}</span>
                </div>
                <ProgressBar value={budgetUsed} max={budgetPlan.total} />
                <div className="stat-sub">
                  Fixed AED {fmtInt(fixedTotal)} + card spend AED {fmtInt(monthTotal)} · {fmtInt(pct(budgetUsed, budgetPlan.total))}% used
                </div>
                <div className="stat-sub">
                  AED {fmtInt(Math.abs(budgetPlan.total - budgetUsed))} {budgetUsed > budgetPlan.total ? 'over budget' : 'left'}
                </div>
              </button>
            </div>

            <div className="period-grid mb-16">
              <PeriodCard title="Today" sub={new Date().toLocaleDateString('en-AE', { day: 'numeric', month: 'short' })} txs={todayTxs} total={todayTotal} />
              <PeriodCard title="This week" sub={getWeekLabel()} txs={weekTxs} total={weekTotal} />
              <PeriodCard title="This month" sub={`${curPeriod.label} (19th–18th)`} txs={monthTxs} total={monthTotal} />
            </div>

            <div className="type-note">💳 Card transactions only — account transfers shown in <button className="btn-link" onClick={() => setTab('flows')}>Account Flows</button></div>

            <div className="card">
              <h3 className="card-hdr-text">Today's transactions</h3>
              {todayTxs.length === 0 ? (
                <div className="empty">No transactions today yet.</div>
              ) : (
                <TxList txs={todayTxs} correcting={correcting} setCorrecting={setCorrecting} applyCorrection={applyCorrection} />
              )}
            </div>
          </div>
        )}

        {/* ══ ACCOUNT FLOWS ══════════════════════════════════════════════ */}
        {tab === 'flows' && (
          <div>
            <div className="page-hdr">
              <div>
                <h2>Account flows</h2>
                <div className="page-sub">Transfers & movements — {expPeriod.label} (19th–18th)</div>
              </div>
              <div className="row-gap">
                <button className="btn-secondary" onClick={() => {
                  const d = new Date(expPeriod.year, expPeriod.month - 1, 20);
                  setExpPeriod(getExpenseMonth(d));
                }}>← Prev</button>
                <button className="btn-secondary" onClick={() => setExpPeriod(getExpenseMonth())}>Current</button>
              </div>
            </div>

            <div className="type-note">🏦 Account transactions only — not counted as spend. Card spend shown in <button className="btn-link" onClick={() => setTab('home')}>Overview</button></div>

            <div className="stat-grid">
              <StatCard label="Total account outflows" value={`AED ${fmt(monthAcctTotal)}`} sub={`${monthAcctFlows.length} transactions`} />
              <StatCard label="Card spend (comparison)" value={`AED ${fmt(monthTotal)}`} sub="actual expenses" color="var(--accent)" />
            </div>

            {/* Salary inflows */}
            {salaryTxs.filter(t => isInExpensePeriod(t.tx_date, expPeriod.year, expPeriod.month)).length > 0 && (
              <div className="card mb-16">
                <h3 className="card-hdr-text">Salary received</h3>
                <TxList txs={salaryTxs.filter(t => isInExpensePeriod(t.tx_date, expPeriod.year, expPeriod.month))} correcting={correcting} setCorrecting={setCorrecting} applyCorrection={applyCorrection} />
              </div>
            )}

            {/* Account outflows */}
            <div className="card">
              <h3 className="card-hdr-text">Account outflows this period</h3>
              {monthAcctFlows.length === 0 ? (
                <div className="empty">No account transactions this period.</div>
              ) : (
                <TxList txs={monthAcctFlows} correcting={correcting} setCorrecting={setCorrecting} applyCorrection={applyCorrection} />
              )}
            </div>
          </div>
        )}

        {/* ══ CASH POSITION ════════════════════════════════════════════════ */}
        {tab === 'cash' && (
          <div>
            <div className="page-hdr">
              <div>
                <h2>Cash position</h2>
                <div className="page-sub">Net liquid position across all accounts</div>
              </div>
              <button className="btn-secondary" onClick={() => setShowCash(true)}>Update balances</button>
            </div>

            <div className="stat-grid">
              <StatCard label="Total liquid position" value={`AED ${fmt(cashPosition)}`} color="var(--success)" />
            </div>

            <div className="card">
              <h3 className="card-hdr-text">Account balances</h3>
              <div className="cash-list">
                {CASH_ACCOUNTS.map(acc => {
                  const isAuto     = acc.auto;
                  const smsVal     = smsBalances[acc.key];
                  const override   = cashOverrides[acc.key];
                  const manVal     = manualCash[acc.key];
                  const displayAmt = isAuto ? (override !== undefined ? override : smsVal?.amount || 0) : manVal;
                  const isOverride = isAuto && override !== undefined;
                  return (
                    <div key={acc.key} className="cash-row">
                      <div className="cash-left">
                        <span className="cash-label">{acc.label}</span>
                        <span className="cash-meta">
                          {isAuto ? (isOverride ? 'manual override' : smsVal ? `from SMS · ${new Date(smsVal.timestamp).toLocaleDateString('en-AE')}` : 'no SMS yet') : 'manually entered'}
                        </span>
                      </div>
                      <div className="cash-right">
                        <span className="cash-amt">AED {fmt(displayAmt)}</span>
                        {isAuto && isOverride && (
                          <button className="btn-link" onClick={() => setCashOverrides(prev => { const n = { ...prev }; delete n[acc.key]; return n; })}>reset to SMS</button>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="cash-total-row">
                  <span className="cash-label">Net position</span>
                  <span className="cash-amt text-success">AED {fmt(cashPosition)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ BUDGET & TRENDS ══════════════════════════════════════════════ */}
        {tab === 'budget' && (
          <div>
            <div className="page-hdr">
              <div>
                <h2>Budget & trends</h2>
                <div className="page-sub">{curPeriod.label} (19th–18th)</div>
              </div>
            </div>

            <BudgetEditor plan={budgetPlan} onChange={updateBudgetPlan} spendByMain={monthByMain} monthTotal={monthTotal} />

            <div className="card mb-16">
              <h3 className="card-hdr-text">Monthly spend — last 6 months</h3>
              <div style={{ height: 260, marginTop: 16 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: 'var(--muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'var(--muted)', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<TrendTooltip />} cursor={{ fill: 'var(--surface2)' }} />
                    <Legend iconType="circle" iconSize={8} formatter={v => <span style={{ color: 'var(--text2)', fontSize: 12 }}>{v}</span>} />
                    {MAIN_CATEGORIES.map((c, i) => (
                      <Bar key={c} dataKey={c} stackId="spend" isAnimationActive={false} fill={CATEGORY_COLORS[c]} stroke="var(--surface)" strokeWidth={2}
                        radius={i === MAIN_CATEGORIES.length - 1 ? [4, 4, 0, 0] : 0} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="stat-grid mb-16">
              <StatCard label="Avg monthly spend" value={`AED ${fmt(trendData.reduce((s, d) => s + d.spend, 0) / Math.max(trendData.filter(d => d.spend > 0).length, 1))}`} sub="last 6 months" />
              <StatCard label="Highest month" value={`AED ${fmt(Math.max(...trendData.map(d => d.spend)))}`} />
              <StatCard label="Lowest month" value={`AED ${fmt(Math.min(...trendData.filter(d => d.spend > 0).map(d => d.spend)))}`} />
            </div>

            {/* Category trend */}
            <div className="card">
              <h3 className="card-hdr-text">Top categories this month</h3>
              <div className="cat-list">
                {Object.entries(byCat(monthTxs)).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([key, amt]) => {
                  const [cat, sub] = key.split('|');
                  return (
                    <div key={key} className="cat-row">
                      <span className="cat-name">{cat} <span className="text-muted">/ {sub}</span></span>
                      <div className="cat-bar-wrap"><div className="cat-bar-fill" style={{ width: `${pct(amt, monthTotal)}%` }} /></div>
                      <span className="cat-amt">AED {fmt(amt)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ══ TRANSACTIONS ═════════════════════════════════════════════════ */}
        {tab === 'txns' && (
          <AllTransactions
            transactions={transactions}
            correcting={correcting}
            setCorrecting={setCorrecting}
            applyCorrection={applyCorrection}
            onAddManual={() => setShowManual(true)}
          />
        )}

        {/* ══ SETTINGS ═════════════════════════════════════════════════════ */}
        {tab === 'settings' && (
          <Settings
            corrections={corrections}
            smsLog={smsLog}
            salary={salary}
            setSalary={setSalary}
            onDisconnect={() => { clearConfig(); setConfigured(false); setTx([]); setSmsLog([]); setCorrections([]); }}
          />
        )}
      </main>

      {/* ── MANUAL TRANSACTION MODAL ── */}
      {showManual && (
        <ManualTxModal
          onClose={() => setShowManual(false)}
          onSave={async (tx) => {
            await writeManualTx(tx);
            setTx(prev => [...prev, tx]);
            setShowManual(false);
            notify('Transaction added');
          }}
        />
      )}

      {/* ── CASH UPDATE MODAL ── */}
      {showCash && (
        <CashModal
          smsBalances={smsBalances}
          cashOverrides={cashOverrides}
          manualCash={manualCash}
          onClose={() => setShowCash(false)}
          onSave={(key, val, isManual) => {
            if (isManual) updateManualCash(key, val);
            else setCashOverrides(prev => ({ ...prev, [key]: parseFloat(val) || 0 }));
          }}
        />
      )}
    </div>
  );
}

// ── PERIOD CARD (main page) ───────────────────────────────────────────────────
function PeriodCard({ title, sub, txs, total }) {
  return (
    <div className="card">
      <div className="period-hdr">
        <div>
          <div className="stat-label">{title}</div>
          <div className="stat-sub">{sub}</div>
        </div>
        <div className="period-total">AED {fmt(total)}</div>
      </div>
      <CategoryBreakdown txs={txs} />
    </div>
  );
}

// ── CATEGORY BREAKDOWN — six main categories, click to expand subcategories ──
function CategoryBreakdown({ txs }) {
  const [open, setOpen] = useState({});
  const groups = useMemo(() => {
    const g = Object.fromEntries(MAIN_CATEGORIES.map(c => [c, { total: 0, subs: {} }]));
    for (const t of txs) {
      const main = mainCategory(t.category);
      // Categories folded into Other keep their own name so they stay identifiable
      const sub  = t.category === main ? t.subcategory : `${t.category} · ${t.subcategory}`;
      g[main].total += aed(t);
      g[main].subs[sub] = (g[main].subs[sub] || 0) + aed(t);
    }
    return g;
  }, [txs]);

  return (
    <div className="maincat-list">
      {MAIN_CATEGORIES.map(c => {
        const subs   = Object.entries(groups[c].subs).sort((a, b) => b[1] - a[1]);
        const isOpen = !!open[c] && subs.length > 0;
        return (
          <div key={c}>
            <button className="maincat-row" disabled={!subs.length} aria-expanded={isOpen}
              onClick={() => setOpen(p => ({ ...p, [c]: !p[c] }))}>
              <span className="maincat-caret">{subs.length ? (isOpen ? '▾' : '▸') : ''}</span>
              <span className="cat-dot" style={{ background: CATEGORY_COLORS[c] }} />
              <span className="cat-name">{c}</span>
              <span className={`cat-amt ${groups[c].total ? '' : 'text-muted'}`}>AED {fmt(groups[c].total)}</span>
            </button>
            {isOpen && (
              <div className="subcat-list">
                {subs.map(([s, amt]) => (
                  <div key={s} className="subcat-row">
                    <span>{s}</span>
                    <span>AED {fmt(amt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── AMOUNT INPUT — edits a number, commits on blur / Enter ───────────────────
function AmountInput({ value, onCommit, label }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const v = parseFloat(draft);
    if (!isNaN(v) && v >= 0 && v !== value) onCommit(v);
    else setDraft(String(value));
  };
  return (
    <input type="number" inputMode="decimal" min="0" className="amount-inp" aria-label={label}
      value={draft} onChange={e => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
  );
}

// ── BUDGET EDITOR ─────────────────────────────────────────────────────────────
function BudgetEditor({ plan, onChange, spendByMain, monthTotal }) {
  const fixed     = plan.items.reduce((s, i) => s + i.amount, 0);
  const used      = fixed + monthTotal;
  const remaining = plan.total - used;
  const setItem = (key, amount) => onChange({ ...plan, items: plan.items.map(i => i.key === key ? { ...i, amount } : i) });

  return (
    <div className="card mb-16">
      <div className="card-hdr">
        <h3>Monthly budget</h3>
        <button className="btn-link" onClick={() => { if (window.confirm('Reset all budget amounts to defaults?')) onChange(DEFAULT_BUDGET); }}>reset to defaults</button>
      </div>

      <div className="budget-line budget-line-total">
        <div className="budget-line-main">
          <span className="budget-line-label">Total budget</span>
          <span className="stat-sub">Fixed AED {fmtInt(fixed)} + MTD card spend AED {fmtInt(monthTotal)} · {fmtInt(pct(used, plan.total))}% used</span>
          <ProgressBar value={used} max={plan.total} />
        </div>
        <AmountInput value={plan.total} label="Total budget" onCommit={v => onChange({ ...plan, total: v })} />
      </div>

      <div className="stat-label mt-12">Fixed expenses (AED)</div>
      {plan.items.map(i => {
        const spent = i.category ? spendByMain[i.category] || 0 : null;
        return (
          <div key={i.key} className="budget-line">
            <div className="budget-line-main">
              <span className="budget-line-label">{i.label}</span>
              {spent !== null && (
                <>
                  <span className={`stat-sub ${spent > i.amount ? 'text-danger' : ''}`}>AED {fmtInt(spent)} card spend this month</span>
                  <ProgressBar value={spent} max={i.amount} />
                </>
              )}
            </div>
            <AmountInput value={i.amount} label={i.label} onCommit={v => setItem(i.key, v)} />
          </div>
        );
      })}

      <div className="income-divider" />
      <div className="income-row"><span className="income-label">Fixed expenses</span><span className="income-val">AED {fmtInt(fixed)}</span></div>
      <div className="income-row"><span className="income-label">Card spend this month</span><span className="income-val">AED {fmtInt(monthTotal)}</span></div>
      <div className="income-row income-total">
        <span className="income-label">Remaining</span>
        <span className={`income-val ${remaining < 0 ? 'text-danger' : 'text-success'}`}>AED {fmtInt(remaining)}</span>
      </div>
      <div className="hint mt-12">Amounts save as soon as you leave a field. Stored in this browser only.</div>
    </div>
  );
}

// ── TREND TOOLTIP ─────────────────────────────────────────────────────────────
function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="chart-tip">
      <div className="chart-tip-hdr"><span>{label}</span><span>AED {fmtInt(row.spend)}</span></div>
      {[...MAIN_CATEGORIES].reverse().map(c => (
        <div key={c} className="chart-tip-row">
          <span className="cat-dot" style={{ background: CATEGORY_COLORS[c] }} />
          <span className="cat-name">{c}</span>
          <span>AED {fmtInt(row[c] || 0)}</span>
        </div>
      ))}
    </div>
  );
}

// ── TX LIST COMPONENT ─────────────────────────────────────────────────────────
function TxList({ txs, correcting, setCorrecting, applyCorrection }) {
  if (!txs.length) return <div className="empty">No transactions.</div>;
  return (
    <div className="tx-list">
      {txs.sort((a, b) => (b.tx_date || '').localeCompare(a.tx_date || '')).map(tx => {
        const acc = ACCOUNTS[tx.source] || { label: tx.source, color: '#6c7a9c' };
        return (
          <div key={tx.id} className="tx-row">
            <div className="tx-left">
              <div className="tx-desc">{tx.description || 'Transaction'}</div>
              <div className="tx-meta">
                <span className="tx-date">{tx.display_date || tx.tx_date}</span>
                <span className="tag" style={{ background: acc.color + '20', color: acc.color, borderColor: acc.color + '40' }}>{acc.label}</span>
                {correcting === tx.id ? (
                  <select autoFocus defaultValue={`${tx.category}|${tx.subcategory}`}
                    onChange={e => { const [c, s] = e.target.value.split('|'); applyCorrection(tx.id, c, s); }}
                    onBlur={() => setCorrecting(null)} className="sel-inline">
                    {CATEGORIES.map(c => (
                      <option key={`${c.category}|${c.subcategory}`} value={`${c.category}|${c.subcategory}`}>
                        {c.category} / {c.subcategory}
                      </option>
                    ))}
                  </select>
                ) : (
                  <button className="tag-btn" onClick={() => setCorrecting(tx.id)}
                    style={{ background: (tx.corrected ? '#10b981' : '#4f6ef7') + '20', color: tx.corrected ? '#10b981' : '#4f6ef7', borderColor: (tx.corrected ? '#10b981' : '#4f6ef7') + '40' }}>
                    {tx.category} / {tx.subcategory} {tx.corrected ? '✓' : '✎'}
                  </button>
                )}
              </div>
            </div>
            <div className="tx-right">
              <div className={`tx-amount ${tx.direction === 'credit' ? 'text-success' : ''}`}>
                {tx.direction === 'credit' ? '+' : '−'}{tx.currency} {(tx.amount || 0).toLocaleString('en-AE', { minimumFractionDigits: 2 })}
                {tx.currency !== 'AED' && tx.amount_aed ? <div className="tx-converted">≈ AED {(tx.amount_aed).toLocaleString('en-AE', { minimumFractionDigits: 2 })}</div> : null}
              </div>
              {tx.balance && <div className="tx-bal">Bal: AED {Math.round(tx.balance).toLocaleString()}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ALL TRANSACTIONS VIEW ─────────────────────────────────────────────────────
function AllTransactions({ transactions, correcting, setCorrecting, applyCorrection, onAddManual }) {
  const [filterBank, setFBank] = useState('all');
  const [filterCat,  setFCat]  = useState('all');
  const [filterType, setFType] = useState('all');
  const [search,     setSearch] = useState('');
  const uniqueBanks = [...new Set(transactions.map(t => t.source))];
  const uniqueCats  = [...new Set(transactions.map(t => t.category))];
  const filtered = transactions
    .filter(t => t.direction === 'debit')
    .filter(t => filterType === 'all' || (filterType === 'cards' ? isCardTx(t) : !isCardTx(t)))
    .filter(t => filterBank === 'all' || t.source === filterBank)
    .filter(t => filterCat  === 'all' || t.category === filterCat)
    .filter(t => !search || t.description?.toLowerCase().includes(search.toLowerCase()));

  const cardTotal = filtered.filter(isCardTx).reduce((s, t) => s + aed(t), 0);
  const acctTotal = filtered.filter(t => !isCardTx(t)).reduce((s, t) => s + aed(t), 0);

  return (
    <div>
      <div className="page-hdr">
        <h2>All transactions</h2>
        <button className="btn-secondary" onClick={onAddManual}>+ Add transaction</button>
      </div>

      {/* Side by side totals */}
      <div className="stat-grid mb-16">
        <StatCard label="Card spend" value={`AED ${fmt(cardTotal)}`} color="var(--accent)" />
        <StatCard label="Account outflows" value={`AED ${fmt(acctTotal)}`} color="var(--warning)" />
      </div>

      <div className="filter-bar">
        <div className="type-toggle">
          {[['all','All'],['cards','Cards'],['accounts','Accounts']].map(([id, lbl]) => (
            <button key={id} className={`toggle-btn ${filterType === id ? 'active' : ''}`} onClick={() => setFType(id)}>{lbl}</button>
          ))}
        </div>
        <input className="search-inp" placeholder="Search description…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="sel-inline" value={filterBank} onChange={e => setFBank(e.target.value)}>
          <option value="all">All accounts</option>
          {uniqueBanks.map(b => <option key={b} value={b}>{ACCOUNTS[b]?.label || b}</option>)}
        </select>
        <select className="sel-inline" value={filterCat} onChange={e => setFCat(e.target.value)}>
          <option value="all">All categories</option>
          {uniqueCats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <TxList txs={filtered} correcting={correcting} setCorrecting={setCorrecting} applyCorrection={applyCorrection} />
    </div>
  );
}

// ── SETTINGS VIEW ─────────────────────────────────────────────────────────────
function Settings({ corrections, smsLog, salary, setSalary, onDisconnect }) {
  const [salaryInput, setSalaryInput] = useState(salary);
  return (
    <div>
      <h2 className="mb-16">Settings</h2>

      <div className="card mb-16">
        <h3 className="card-hdr-text">Salary</h3>
        <div className="row-gap" style={{ marginTop: 8 }}>
          <input type="number" className="sel-inline" value={salaryInput} onChange={e => setSalaryInput(e.target.value)} placeholder="Monthly salary AED" style={{ flex: 1 }} />
          <button className="btn-primary" onClick={() => setSalary(parseFloat(salaryInput) || 0)}>Save</button>
        </div>
        <div className="hint" style={{ marginTop: 6 }}>Current: AED {salary.toLocaleString()}</div>
      </div>

      <div className="card mb-16">
        <h3 className="card-hdr-text">Your accounts</h3>
        {Object.entries(ACCOUNTS).map(([key, info]) => (
          <div key={key} className="settings-row">
            <span className="bank-dot" style={{ background: info.color }} />
            <span style={{ flex: 1, fontSize: 13 }}>{info.label}</span>
            {info.owner && <span className="tag tag-gray">{info.owner}</span>}
          </div>
        ))}
      </div>

      <div className="card mb-16">
        <h3 className="card-hdr-text">Correction rules ({corrections.length})</h3>
        <p className="card-sub">Applied automatically to future transactions.</p>
        {corrections.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 13 }}>No rules yet. Re-tag a transaction to create one.</div>
        ) : corrections.map((c, i) => (
          <div key={i} className="settings-row">
            <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, color: 'var(--text2)' }}>"{c.description_pattern}"</span>
            <span className="text-muted" style={{ fontSize: 11 }}>→</span>
            <span className="tag tag-accent">{c.new_category} / {c.new_subcategory}</span>
          </div>
        ))}
      </div>

      <div className="card mb-16">
        <h3 className="card-hdr-text">SMS log (last 8)</h3>
        {smsLog.slice(-8).reverse().map((s, i) => (
          <div key={i} className="sms-row">
            <div className="sms-hdr">
              <span className={`tag ${s.status === 'pending' ? 'tag-warn' : 'tag-success'}`}>{s.status}</span>
              <span className="text-muted">{s.bank}</span>
              <span className="text-muted">{String(s.timestamp || '').slice(0, 16)}</span>
            </div>
            <div className="sms-body">{s.body}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 className="card-hdr-text">Connection</h3>
        <p className="card-sub" style={{ marginBottom: 12 }}>Clear stored credentials from this browser.</p>
        <button className="btn-danger" onClick={onDisconnect}>Disconnect & clear credentials</button>
      </div>
    </div>
  );
}

// ── MANUAL TX MODAL ───────────────────────────────────────────────────────────
function ManualTxModal({ onClose, onSave }) {
  const today = new Date();
  const todayStr = `${String(today.getDate()).padStart(2,'0')}/${String(today.getMonth()+1).padStart(2,'0')}/${today.getFullYear()}`;
  const [form, setForm] = useState({
    description: '', amount: '', currency: 'AED', direction: 'debit',
    category: 'Other', subcategory: 'Miscellaneous', source: 'Manual', tx_date: todayStr,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const save = () => {
    if (!form.description || !form.amount) return;
    onSave({ ...form, id: `TXN-M-${Date.now()}`, amount: parseFloat(form.amount), amount_aed: parseFloat(form.amount), timestamp: new Date().toISOString(), origin: 'Manual', corrected: false, confidence: 1 });
  };
  const uniqueCats = [...new Set(CATEGORIES.map(c => c.category))];
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-hdr">
          <h3>Add transaction</h3>
          <button className="btn-ghost icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="field">
          <label>Description</label>
          <input type="text" value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Cash groceries" />
        </div>
        <div className="row-gap">
          <div className="field" style={{ flex: 1 }}>
            <label>Amount</label>
            <input type="number" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
          </div>
          <div className="field" style={{ width: 90 }}>
            <label>Currency</label>
            <select value={form.currency} onChange={e => set('currency', e.target.value)}>
              <option>AED</option><option>USD</option><option>GBP</option><option>EUR</option><option>PKR</option><option>CAD</option>
            </select>
          </div>
        </div>
        <div className="row-gap">
          <div className="field" style={{ flex: 1 }}>
            <label>Category</label>
            <select value={form.category} onChange={e => { set('category', e.target.value); set('subcategory', CATEGORIES.find(c => c.category === e.target.value)?.subcategory || ''); }}>
              {uniqueCats.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Subcategory</label>
            <select value={form.subcategory} onChange={e => set('subcategory', e.target.value)}>
              {CATEGORIES.filter(c => c.category === form.category).map(c => <option key={c.subcategory}>{c.subcategory}</option>)}
            </select>
          </div>
        </div>
        <div className="row-gap">
          <div className="field" style={{ flex: 1 }}>
            <label>Date (DD/MM/YYYY)</label>
            <input type="text" value={form.tx_date} onChange={e => set('tx_date', e.target.value)} placeholder="19/07/2026" />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Type</label>
            <select value={form.direction} onChange={e => set('direction', e.target.value)}>
              <option value="debit">Expense (debit)</option>
              <option value="credit">Income (credit)</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save}>Save transaction</button>
        </div>
      </div>
    </div>
  );
}

// ── CASH MODAL ────────────────────────────────────────────────────────────────
function CashModal({ smsBalances, cashOverrides, manualCash, onClose, onSave }) {
  const [vals, setVals] = useState(() => {
    const init = {};
    for (const acc of CASH_ACCOUNTS) {
      if (acc.auto) init[acc.key] = cashOverrides[acc.key] !== undefined ? cashOverrides[acc.key] : smsBalances[acc.key]?.amount || 0;
      else init[acc.key] = manualCash[acc.key] || 0;
    }
    return init;
  });
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-hdr">
          <h3>Update balances</h3>
          <button className="btn-ghost icon-btn" onClick={onClose}>✕</button>
        </div>
        {CASH_ACCOUNTS.map(acc => (
          <div key={acc.key} className="field">
            <label>{acc.label} {acc.auto ? '(auto from SMS — override if needed)' : '(manual)'}</label>
            <input type="number" value={vals[acc.key]} onChange={e => setVals(p => ({ ...p, [acc.key]: e.target.value }))} placeholder="0.00" />
          </div>
        ))}
        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => {
            for (const acc of CASH_ACCOUNTS) onSave(acc.key, vals[acc.key], !acc.auto);
            onClose();
          }}>Save balances</button>
        </div>
      </div>
    </div>
  );
}